import { datadogRum } from "@datadog/browser-rum";
import Tabs from "./Tabs";
import Button from "./Button";
import Tooltip from "./Tooltip";
import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { flattenKeys } from "../../utils/index.jsx";
import Callout from "./Callout";
import FormikSubmitButton from "./FormikSubmitButton";
import styles from "./TabbedForm.module.css";
import { getIn, useFormikContext } from "formik";
import { ModalDialogFooter } from "./ModalDialog";
import { RumActions } from "../../utils/constants/rum_actions";

const TabbedFormContext = createContext({});

const TabbedForm = forwardRef(({ children, id }, ref) => {
  const { errors: formikErrors, touched: formikTouched } = useFormikContext();
  const [tabsInError, setTabsInError] = useState([]);
  const addTabInError = useCallback((tabId) => {
    setTabsInError((cur) => {
      if (cur.includes(tabId)) {
        return cur;
      }
      return [...cur, tabId];
    });
  }, []);
  const removeTabInError = useCallback((tabId) => {
    setTabsInError((cur) => cur.filter((id) => tabId !== id));
  }, []);

  const [panelInputs, setPanelInputs] = useState({});
  const registerPanelInputs = useCallback((panelId, inputs) => {
    setPanelInputs((cur) => {
      const newPanelInputs = { ...cur };
      newPanelInputs[panelId] = inputs;
      return newPanelInputs;
    });
  }, []);
  const unregisterPanelInputs = useCallback((panelId) => {
    setPanelInputs((cur) => {
      const newPanelInputs = { ...cur };
      delete newPanelInputs[panelId];
      return newPanelInputs;
    });
  }, []);

  const errors = useMemo(() => flattenKeys(formikErrors), [formikErrors]);
  const touched = useMemo(() => flattenKeys(formikTouched), [formikTouched]);

  return (
    <>
      <Tabs id={id} ref={ref}>
        <TabbedFormContext.Provider
          value={{
            formId: id,
            errors,
            touched,
            panelInputs,
            tabsInError,
            addTabInError,
            removeTabInError,
            registerPanelInputs,
            unregisterPanelInputs,
          }}
        >
          {children}
        </TabbedFormContext.Provider>
      </Tabs>
    </>
  );
});

function Actions({ error, disableNext = false, disableNextReason, sticky }) {
  const { isFirstTab, isLastTab, selectNextTab, selectPreviousTab } =
    Tabs.useActiveTab();
  const { tabsInError, errors, panelInputs } = useContext(TabbedFormContext);
  const { errors: formikErrors, submitCount } = useFormikContext();

  // Errors on fields that no tab renders would otherwise be invisible:
  // no red tab, no inline message, and a submit button that silently does nothing.
  const hiddenErrorMessages = useMemo(() => {
    const visibleInputs = new Set(Object.values(panelInputs).flat());
    return errors
      .filter((key) => !visibleInputs.has(key))
      .map((key) => getIn(formikErrors, key))
      .filter((message) => typeof message === "string");
  }, [errors, panelInputs, formikErrors]);
  const showHiddenErrors = submitCount > 0 && hiddenErrorMessages.length > 0;

  const nextButton = (
    <Button type="button" onClick={selectNextTab} disabled={disableNext}>
      Suivant
    </Button>
  );
  const submitButton = (
    <FormikSubmitButton disabled={tabsInError.length > 0 || disableNext}>
      Valider
    </FormikSubmitButton>
  );

  const primaryAction = isLastTab ? submitButton : nextButton;

  const primaryWithTooltip =
    disableNext && disableNextReason ? (
      <Tooltip content={disableNextReason}>{primaryAction}</Tooltip>
    ) : (
      primaryAction
    );

  const errorCallouts = (
    <>
      {(tabsInError.length > 0 || showHiddenErrors) && (
        <Callout variant="danger">
          Il y a des erreurs dans le formulaire
          {showHiddenErrors && (
            <ul>
              {hiddenErrorMessages.map((message) => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          )}
        </Callout>
      )}
      <Callout variant="danger">{error}</Callout>
    </>
  );

  if (sticky) {
    return (
      <>
        {errorCallouts}
        <ModalDialogFooter
          start={
            !isFirstTab && (
              <Button
                type="button"
                variant="secondary"
                onClick={selectPreviousTab}
              >
                Précédent
              </Button>
            )
          }
        >
          {primaryWithTooltip}
        </ModalDialogFooter>
      </>
    );
  }

  return (
    <div className={styles.actionsContainer}>
      <div className={styles.actions}>
        <Button
          type="button"
          variant="secondary"
          onClick={selectPreviousTab}
          disabled={isFirstTab}
        >
          Précédent
        </Button>
        <div className={styles.rightActions}>{primaryWithTooltip}</div>
      </div>
      {errorCallouts}
    </div>
  );
}

function TabPanel({ children, id, forceRenderInactive = false, ...rest }) {
  const {
    formId,
    errors,
    touched,
    panelInputs,
    addTabInError,
    removeTabInError,
    registerPanelInputs,
    unregisterPanelInputs,
  } = useContext(TabbedFormContext);
  const { activeTab } = Tabs.useActiveTab();
  const tabRef = useRef();

  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (activeTab === id) {
      datadogRum.addAction(RumActions.FORM_TAB_VIEWED, {
        form: formId,
        tab: id,
      });
    }
  }, [activeTab, id, formId]);

  useEffect(() => {
    if (!tabRef.current) return;
    const inputs = Array.from(tabRef.current.querySelectorAll("[name]")).map(
      (elem) => elem.name,
    );
    registerPanelInputs(id, inputs);
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsInitialized(true);
    return () => {
      unregisterPanelInputs(id);
    };
  }, [id, registerPanelInputs, unregisterPanelInputs]);

  useEffect(() => {
    let inputs = panelInputs[id] ?? [];
    if (tabRef.current) {
      inputs = Array.from(tabRef.current.querySelectorAll("[name]")).map(
        (elem) => elem.name,
      );
    }

    let hasError = false;
    for (const input of inputs) {
      if (errors.includes(input) && touched.includes(input)) {
        hasError = true;
        break;
      }
    }

    if (hasError) {
      addTabInError(id);
    } else {
      removeTabInError(id);
    }
  }, [errors, touched, panelInputs, id, addTabInError, removeTabInError]);

  return (
    <Tabs.TabPanel
      id={id}
      ref={tabRef}
      shouldRenderInactive={!isInitialized || forceRenderInactive}
      {...rest}
    >
      {children}
    </Tabs.TabPanel>
  );
}

function Tab({ children, panelId, ...rest }) {
  const { tabsInError } = useContext(TabbedFormContext);
  const hasError = tabsInError.includes(panelId);
  return (
    <Tabs.Tab panelId={panelId} type={hasError ? "error" : undefined} {...rest}>
      <span>{children}</span>
    </Tabs.Tab>
  );
}

TabbedForm.TabList = Tabs.TabList;
TabbedForm.TabPanel = TabPanel;
TabbedForm.Tab = Tab;
TabbedForm.Actions = Actions;

export default TabbedForm;
